import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type IncomeStatus = "RECEIVED" | "CANCELLED";

export type FinanceIncome = {
  id: string;
  code: string;
  receivedDate: string;
  companyId: string;
  company: { id: string; code: string; name: string };
  accountId: string;
  account: { id: string; code: string; name: string; currency: string };
  clientCompanyId: string | null;
  clientCompany: { id: string; name: string } | null;
  amount: string;
  currency: string;
  creditedAmount: string;
  creditedCurrency: string;
  rate: string | null;
  reference: string | null;
  description: string | null;
  notes: string | null;
  status: IncomeStatus;
  cancelReason: string | null;
  cancelledAt: string | null;
};

export type IncomeOptions = {
  accounts: {
    id: string;
    code: string;
    name: string;
    currency: string;
    type: string;
    company: { id: string; name: string };
  }[];
  clients: { id: string; name: string }[];
};

const INCOME_KEY = ["finance-income"] as const;

export function useIncomeList(filters: {
  companyId?: string;
  status?: IncomeStatus | "";
}): UseQueryResult<FinanceIncome[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...INCOME_KEY, "list", filters, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ incomes: FinanceIncome[] }>("/finance-income", {
        token: accessToken,
        query: { companyId: filters.companyId || undefined, status: filters.status || undefined },
      });
      return res.incomes;
    },
  });
}

export function useIncomeOptions(): UseQueryResult<IncomeOptions> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...INCOME_KEY, "options", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () => apiFetch<IncomeOptions>("/finance-income/options", { token: accessToken }),
  });
}

export function useIncomeAction() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ method, path, body }: { method: "POST" | "PUT"; path: string; body: unknown }) =>
      apiFetch(`/finance-income${path}`, { method, token: accessToken, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INCOME_KEY });
      // Income moves account balances.
      queryClient.invalidateQueries({ queryKey: ["finance-accounts"] });
    },
  });
}
